import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { businessProfileSchema, type BusinessProfileInput } from '@koyomi/shared';
import { Button, Card, Input, MaskedInput, PageHeader, PageLoading, Select, Switch } from '@koyomi/ui';
import { api } from '../../lib/api.ts';
import { useBusiness, useSave, type BusinessDto } from '../../lib/queries.ts';

const TIMEZONES = ['America/Sao_Paulo', 'America/Manaus', 'America/Belem', 'America/Fortaleza', 'America/Recife', 'America/Bahia', 'America/Cuiaba', 'America/Campo_Grande', 'America/Porto_Velho', 'America/Rio_Branco', 'America/Noronha', 'America/Lisbon', 'America/New_York', 'Europe/London'];

export function BusinessPage() {
  const { t } = useTranslation(['config', 'common']);
  const business = useBusiness();
  const save = useSave((input: BusinessProfileInput) => api<BusinessDto>('/business', { method: 'PATCH', body: input }), ['business']);
  const saveFlags = useSave((flags: Partial<BusinessDto['featureFlags']>) => api('/business/feature-flags', { method: 'PATCH', body: flags }), ['business'], { silent: true });
  const form = useForm<BusinessProfileInput>({ resolver: zodResolver(businessProfileSchema) });
  useEffect(() => {
    const b = business.data;
    if (b) form.reset({ name: b.name, segment: b.segment ?? '', address: b.address ?? '', phone: b.phone ?? '', timezone: b.timezone, aiLocale: b.aiLocale, modality: b.modality, taxId: b.taxId ?? '' });
  }, [business.data, form]);
  if (business.isLoading) return <><PageHeader title={t('config:business.title')} subtitle={t('config:business.subtitle')} /><PageLoading cards={2} rows={6} /></>;
  if (!business.data) return null;
  const b = business.data;
  // Domicílio não é chave aqui: segue a modalidade (campo ao lado).
  const flags = (Object.keys(b.featureFlags) as (keyof BusinessDto['featureFlags'])[]).filter((f) => f !== 'homeService');
  return (
    <>
      <PageHeader title={t('config:business.title')} subtitle={t('config:business.subtitle')} />
      <div className="k-split">
        <Card>
          <form className="k-stack" onSubmit={form.handleSubmit((v) => save.mutate({ ...v, segment: v.segment || null, address: v.address || null, phone: v.phone || null, taxId: v.taxId || null }))} noValidate>
            <Input label={t('config:business.name')} {...form.register('name')} error={form.formState.errors.name?.message} />
            <div className="k-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              <Input label={t('config:business.segment')} {...form.register('segment')} />
              <MaskedInput mask="phone" label={t('config:business.phone')} {...form.register('phone')} error={form.formState.errors.phone?.message} />
            </div>
            <Input label={t('config:business.address')} {...form.register('address')} />
            <div className="k-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              <Select label={t('config:business.timezone')} {...form.register('timezone')}>
                {[b.timezone, ...TIMEZONES.filter((z) => z !== b.timezone)].map((z) => <option key={z} value={z}>{z}</option>)}
              </Select>
              <Select label={t('config:business.aiLocale')} {...form.register('aiLocale')}>
                <option value="pt-BR">Português (Brasil)</option>
                <option value="en-US">English (US)</option>
              </Select>
              <Select label={t('config:business.modality')} hint={t('config:business.modalityHint')} {...form.register('modality')}>
                {(['LOCAL', 'HOME', 'BOTH'] as const).map((m) => <option key={m} value={m}>{t(`config:business.modalities.${m}`)}</option>)}
              </Select>
              <MaskedInput mask="cpfCnpj" label={t('config:business.taxId')} {...form.register('taxId')} error={form.formState.errors.taxId && t('common:errors.INVALID_TAX_ID')} />
            </div>
            <div className="k-row" style={{ justifyContent: 'flex-end' }}>
              <Button type="submit" variant="primary" loading={save.isPending}>{t('config:common.saveChanges')}</Button>
            </div>
          </form>
        </Card>
        <Card title={t('config:business.features')} subtitle={t('config:business.featuresHint')} className="k-panel">
          <div className="k-stack" style={{ gap: 4 }}>
            {flags.map((f) => (
              <Switch key={f} label={t(`config:business.flags.${f}`)} checked={b.featureFlags[f]} onChange={(v) => saveFlags.mutate({ [f]: v })} />
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
